"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { LazyUpgradeQuoteModal } from "@/components/billing/LazyUpgradeQuoteModal";
import { safeGet, safeSet } from "@/components/onboarding/safe-storage";
import { SHELL_BANNER_CTA, ShellBanner } from "@/components/shell/AppPromoBanner";
import { useAuth } from "@/contexts/auth-context";
import { withDiamondPass, type DiamondPass } from "@/lib/billing/diamond-pass";
import { getEntitledSubscriptionSlugs } from "@/lib/billing/entitled-slugs";
import { planSubscriptionFromRow, routePurchase, type PlanChangeRoute } from "@/lib/billing/purchase-route";
import { DIAMOND_MONTHLY_PRICE_ID } from "@/lib/billing/tier-price-ids";
import { canOpenUpgradeFlowClient } from "@/lib/billing/upgrade-flow-config";
import { phCapture } from "@/utils/analytics";

const LAST_DAY_MS = 86_400_000;

function endDate(endsAt: string): string {
  return new Date(endsAt).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function DiamondWeekBanner() {
  const { isLoading, user, subscriptions, diamondPass } = useAuth();
  if (isLoading || !user || !diamondPass) return null;
  if (getEntitledSubscriptionSlugs(subscriptions).has("diamond")) return null;
  if (withDiamondPass(subscriptions, [diamondPass]).length === subscriptions.length) return null;
  return <DiamondWeek key={`${user.id}:${diamondPass.ends_at}`} userId={user.id} pass={diamondPass} />;
}

function DiamondWeek({ userId, pass }: { userId: string; pass: DiamondPass }) {
  const { user, subscriptions } = useAuth();
  const storageKey = `upsider_diamond_week:${userId}:dismissed`;
  const lastDay = Date.parse(pass.ends_at) - Date.now() <= LAST_DAY_MS;
  const [dismissed, setDismissed] = useState(() => !lastDay && safeGet(storageKey) === pass.ends_at);
  const [upgradeTarget, setUpgradeTarget] = useState<PlanChangeRoute | null>(null);
  const shown = useRef(false);

  const route = routePurchase(subscriptions.map(planSubscriptionFromRow), { priceId: DIAMOND_MONTHLY_PRICE_ID, interval: "month" });
  const planChange = route.kind === "plan_change" && canOpenUpgradeFlowClient(user) ? route : null;
  const modalRoute = upgradeTarget ?? planChange;

  useEffect(() => {
    if (dismissed || shown.current) return;
    shown.current = true;
    phCapture("diamond_week_banner_shown", { last_day: lastDay });
  }, [dismissed, lastDay]);

  function dismiss() {
    safeSet(storageKey, pass.ends_at);
    setDismissed(true);
    phCapture("diamond_week_banner_dismissed", { last_day: lastDay });
  }

  const action = planChange ? (
    <button
      type="button"
      className={SHELL_BANNER_CTA}
      onClick={() => {
        phCapture("diamond_week_banner_upgrade_clicked", { destination: "upgrade_modal" });
        setUpgradeTarget(planChange);
      }}
    >
      Keep Diamond
    </button>
  ) : (
    <Link
      href="/pricing"
      className={SHELL_BANNER_CTA}
      onClick={() => phCapture("diamond_week_banner_upgrade_clicked", { destination: "pricing" })}
    >
      Keep Diamond
    </Link>
  );

  return (
    <>
      <ShellBanner
        open={!dismissed}
        label="Free Diamond week"
        dismissLabel="Dismiss free Diamond week banner"
        onDismiss={dismiss}
        strong={lastDay ? `Free Diamond ends ${endDate(pass.ends_at)}.` : `Free Diamond until ${endDate(pass.ends_at)}.`}
        rest="Your free week opens the +EV Sniper, Whale Watch and Race to Place alerts. Your plan and bill don't change."
        action={action}
      />
      {modalRoute && (
        <LazyUpgradeQuoteModal
          open={upgradeTarget !== null}
          targetTier={modalRoute.targetTier}
          targetInterval={modalRoute.targetInterval}
          source="diamond_week"
          onClose={(result) => {
            setUpgradeTarget(null);
            if (result.type === "success") window.location.reload();
          }}
        />
      )}
    </>
  );
}
